import React, { useContext, useState, useRef } from 'react';
import DetailsSections from '../../components/details-sections';
import Tracker from '../../components/tracker';
import Divider from '../../components/divider';
import Button from '../../components/button';
import Confirm from '../../components/confirm';
import { useAppointmentDetails, useTranslation } from '../../hooks';
import './_manage.scss';
import { useNavigate } from 'react-router';
import Map from '../../components/map/map';
import { AppContext, ServicesContext } from '../../contexts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Zone from '../../components/zone/zone';
import useDateTime from '../../hooks/useDateTime';
import SmsForm from '../../components/smsForm';
import { isProdURL } from '../../utils';

dayjs.extend(utc);
dayjs.extend(timezone);

function Manage() {
  const { current: zoneName } = useRef('apppointment_details_additional_info');
  const { current: smsConsentBlock } = useRef('sms_consent');
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    appointmentData,
    zones,
    appointmentTime,
    smsConsentDetails
  } = useContext(AppContext);
  const { saveZone } = useContext(ServicesContext);
  const [zoneValid, setZoneValid] = useState(false);
  const details = useAppointmentDetails(
    appointmentTime.startTimeInUTC,
    appointmentTime.endTimeInUTC
  );
  const [confirmReschedule, setConfirmReschedule] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isZoneSubmitting, setIsZoneSubmitting] = useState(false);
  const { cancelBooking, confirmAppointment, saveSmsConsent } =
    useContext(ServicesContext);
  const zone = zones?.[zoneName];
  const smsConsentForm = smsConsentDetails?.[smsConsentBlock];
  const { formattedTime, formattedDate } = useDateTime();
  const {
    steps,
    cancelAlowed,
    rescheduleAllowed,
    mapAllowed,
    confirmAllowed,
    origin,
    destination,
    helpLine,
    cancelAllowedInPortal,
    isSmsConsentRequired,
    currentSmsConsent
  } = appointmentData;
  const timeFormat = formattedTime || 'h:mm a';
  const dateFormat = formattedDate ? formattedDate + ', ' : 'DD/MM/YYYY, ';

  const googleApiKey = isProdURL()
    ? process.env.REACT_APP_GOOGLE_API_KEY_PROD
    : process.env.REACT_APP_GOOGLE_API_KEY_DEV;

  const stepsWithDate = steps.map((step) => ({
    ...step,
    subTitle: step.subTitle
      ? dayjs(step.subTitle)
          .tz(dayjs.tz.guess())
          .format(dateFormat + timeFormat)
      : undefined
  }));

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      if (isSmsConsentRequired && smsFormRef.current) {
        await smsFormRef.current.submit();
      }
      setTimeout(async () => {
        const message = await confirmAppointment();
        navigate('/message', {
          state: {
            ...message,
            cta: i18n('cp_track_your_appointment'),
            onCtaClick: navigate.bind(null, '/manage')
          }
        });
      }, 1500);
    } catch (error) {
      console.error('Error during confirmation:', error);
      setIsConfirming(false);
    }
  };

  const handleRescheduleConfirm = () => {
    navigate('/book', { state: { isReschedule: true } });
  };

  const handleRescheduleCancel = () => {
    setConfirmReschedule(false);
  };

  const handleCancelConfirm = async () => {
    const message = await cancelBooking();
    navigate('/message', { state: message });
  };

  const handleCancelCancel = () => {
    setConfirmCancel(false);
  };

  const handleRescheduleOpen = () => setConfirmReschedule(true);

  const handleCancelOpen = () => setConfirmCancel(true);

  const handleZoneSubmit = async (fields) => {
    setIsZoneSubmitting(true);

    const submittedZone = {
      [zoneName]: {
        ...zone,
        fields
      }
    };

    const message = await saveZone(submittedZone);
    navigate('/message', {
      state: {
        ...message,
        cta: i18n('cp_track_your_appointment'),
        onCtaClick: navigate.bind(null, '/manage')
      }
    });
    if (isSmsConsentRequired && smsFormRef.current) {
      await smsFormRef.current.submit();
    }
  };

  const updatedSmsConsentFields = smsConsentForm?.fields?.map((field) => {
    if (field.component === 'switch') {
      return {
        ...field,
        options: {
          ...field.options,
          value: currentSmsConsent
        }
      };
    }
    return field;
  });

  const handleSmsConsent = (fields) => {
    const submittedSmsConsent = {
      [smsConsentBlock]: {
        ...smsConsentForm,
        fields
      }
    };
    (async () => await saveSmsConsent(submittedSmsConsent))();
  };
  const smsFormRef = useRef(null);

  return (
    <div className='container manage'>
      <div className='manage__card card'>
        <DetailsSections
          title={i18n('cp_appointment_details')}
          items={details}
        />
        <Divider />
        <h2 className='manage__section-title'>
          {i18n('cp_progress_tracking')}
        </h2>
        {mapAllowed && googleApiKey && (
          <Map
            origin={origin}
            destination={destination}
            apiKey={googleApiKey}
          />
        )}
        <Tracker items={stepsWithDate} />
        <Divider />
        {zone && (
          <Zone
            disabled={zone.isUserAdditionalInfoSubmitted}
            onValidityChange={setZoneValid}
            title={zone.title}
            copy={zone.copy}
            fields={zone.fields}
            onSubmit={handleZoneSubmit}
            name={zoneName}
          />
        )}
        {isSmsConsentRequired && (
          <SmsForm
            disabled={smsConsentDetails?.sms_consent?.isUserAdditionalInfoSubmitted || false}
            fields={updatedSmsConsentFields}
            onSubmit={handleSmsConsent}
            formRef={smsFormRef}
          />
        )}
        <div className='manage__actions'>
          {zone && !zone.isUserAdditionalInfoSubmitted && (
            <Button
              form={zoneName}
              disabled={!zoneValid}
              className='zone__submit'
              type='submit'
              loading={isZoneSubmitting}
            >
              {i18n('cp_apppointment_details_additional_info_submit')}
            </Button>
          )}
          {((!zone && confirmAllowed) ||
            (zone && confirmAllowed && zone.isUserAdditionalInfoSubmitted)) && (
            <Button
              testId='confirm'
              loading={isConfirming}
              onClick={handleConfirm}
            >
              {i18n('cp_confirm_appointment')}
            </Button>
          )}
          {rescheduleAllowed && (
            <Button
              testId='reschedule'
              onClick={handleRescheduleOpen}
              theme='outline'
            >
              {i18n('cp_reschedule')}
            </Button>
          )}
          {cancelAlowed && (
            <Button testId='cancel' onClick={handleCancelOpen} theme='outline'>
              {i18n('cp_cancel_appointment')}
            </Button>
          )}
        </div>
      </div>
      <Confirm
        title={i18n('cp_reschedule_confirm_title')}
        copy={i18n('cp_reschedule_confirm_copy')}
        cancelText={i18n('cp_cancel')}
        confirmText={i18n('cp_yes_reschedule')}
        isOpen={confirmReschedule}
        onOk={handleRescheduleConfirm}
        onCancel={handleRescheduleCancel}
        testId='reschedule-modal'
      />
      {cancelAllowedInPortal ? (
        <Confirm
          title={i18n('cp_cancel_appointment_confirm_title')}
          copy={i18n('cp_cancel_appointment_confirm_copy')}
          cancelText={i18n('cp_cancel')}
          confirmText={i18n('cp_yes_cancel')}
          isOpen={confirmCancel}
          onOk={handleCancelConfirm}
          onCancel={handleCancelCancel}
          testId='cancel-confirm'
        />
      ) : (
        <Confirm
          title={i18n('cp_cancel_appointment')}
          isOpen={confirmCancel}
          onCancel={handleCancelCancel}
          copy={i18n('cp_cancel_cancel_appointment_offline', {
            phone: `<a href="tel:${helpLine}">${helpLine}</a>`
          })}
          testId='cancel-offline'
        />
      )}
    </div>
  );
}

export default Manage;
